import React, { FC, useCallback, useState } from 'react';
import useTranslator from 'Client/hooks/useTranslator';
import Modal from '@westwing/ui-kit/Modal';
import styled, { css } from 'styled-components';
import { forDesktop } from '@westwing/ui-kit/breakPoints';
import { body2, header5 } from '@westwing/ui-kit/typography';
import { CountryLink } from 'OneWestwingFooter/FooterCountrySelector/useCountrySelectorLinks';
import BellaHybridLink from 'Client/components/Generic/Links/BellaHybridLink';
import ActionButton from '@westwing/ui-kit/ActionButton';
import useAppshellHistoryPush from 'AppShell/hooks/useAppshellHistoryPush';
import useOwwFooterTracking, {
    FOOTER_TRACKING_EVENT_CATEGORY,
} from 'OneWestwingFooter/providers/OwwFooterTrackingProvider';
import { OneWestwingFooterIds } from 'Common/qaIds';

const StyledModalContent = styled(Modal.Content)`
    position: absolute;
    bottom: 0;
    left: 0;
    max-height: 75vh;
    padding: 48px 0px 0px 0px;
    width: 100%;
    ${forDesktop`
        padding: 48px 60px;
        position: relative;
        width: 445px;
        max-height: none;
    `}
`;

const StyledModalHeader = styled(Modal.Header)`
    padding: 0 20px;
    ${header5}
    color: ${({ theme }) => theme.colors.charcoal100};
    font-family: ${({ theme }) => theme.fonts.fontChronicleRoman};
    ${forDesktop`
        padding: 0;
    `}
`;

const StyledModalBody = styled(Modal.Body)`
    padding: 0;
    overflow: auto;

    ${forDesktop`
        overflow: hidden;    
    `}
`;

const CountryList = styled.ul`
    margin: 20px 0 40px;
    ${forDesktop`
        margin: 20px 0 28px;
    `}
`;

const Link = styled(BellaHybridLink)`
    height: 73px;
    padding: 0 15px;
    align-items: center;
    display: flex;
    border-bottom: 1px solid ${({ theme }) => theme.colors.charcoal20New};
    ${forDesktop`
        display: none;
    `}
`;

const Select = styled.button`
    display: none;

    ${forDesktop`
        height: 40px;
        padding: 0 15px;
        align-items: center;
        display: flex;
        width: 100%;
        cursor: pointer;
    `}
`;

const CountryItem = styled.li<{ $selected: boolean }>`
    background: ${({ theme, $selected }) => ($selected ? theme.colors.turquoise10 : theme.colors.white)};
    padding: 0 15px;

    ${({ $selected, theme }) =>
        css`
            ${$selected &&
            css`
                & ${Link} {
                    pointer-events: none;
                }
            `}
            ${forDesktop`
                padding: 0;
                background: ${$selected ? theme.colors.charcoal20New : theme.colors.white};
                &:hover {
                    background: ${$selected ? theme.colors.charcoal20New : theme.colors.charcoal10};
                }
            `}
        `}
`;

export const Country = styled.div`
    ${body2}
    color: ${({ theme }) => theme.colors.charcoal100};
    font-size: 16px;
    display: flex;
    gap: 14px;
    align-items: center;
`;

export const FlagImg = styled.img`
    width: 20px;
    height: 20px;
`;

const StyledModalFooter = styled(Modal.Footer)`
    padding: 0;
    display: none;

    ${forDesktop`
        display: flex;
    `}
`;

interface CountryOverlayProps {
    isOpen: boolean;
    dismissFunction: () => void;
    countryLinks: CountryLink[];
    testId?: string;
}

export const CountryOverlay: FC<CountryOverlayProps> = ({ isOpen, dismissFunction, countryLinks, testId }) => {
    const appshellHistoryPush = useAppshellHistoryPush(() => true);
    const { ga } = useOwwFooterTracking();

    const [selectedCountryIndex, setSelectedCountryIndex] = useState(0);

    const trackSelect = useCallback((country: string) => {
        ga.trackNonInteractiveGenericEvent(FOOTER_TRACKING_EVENT_CATEGORY, {
            action: `country selector - select`,
            label: country,
        });
    }, []);

    const handleCountryClick = (index: number) => {
        trackSelect(countryLinks[selectedCountryIndex].code);
        setSelectedCountryIndex(index);
    };

    const handleRedirect = () => {
        const selectedCountry = countryLinks[selectedCountryIndex];

        // current country is always first in the country list
        if (selectedCountryIndex === 0) {
            dismissFunction();
            return;
        }
        appshellHistoryPush(selectedCountry.link);
    };

    const t = useTranslator();

    return (
        <Modal data-testid={testId} onOpen={() => {}} isOpen={isOpen} onClose={dismissFunction}>
            <StyledModalContent>
                <Modal.CloseButton />
                <StyledModalHeader withCloseButton={false}>{t('WestwingNow_worldwide')}</StyledModalHeader>
                <StyledModalBody>
                    <CountryList role="listbox" aria-label={t('WestwingNow_worldwide')}>
                        {countryLinks.map((country, index) => (
                            <CountryItem $selected={index === selectedCountryIndex} key={country.poKey}>
                                <Link onClick={() => trackSelect(country.code)} to={country.link}>
                                    <Country>
                                        <FlagImg src={country.src} alt={country.poKey} />
                                        <div>{t(country.poKey)}</div>
                                    </Country>
                                </Link>
                                <Select
                                    role="option"
                                    aria-selected={index === selectedCountryIndex}
                                    onClick={() => handleCountryClick(index)}
                                >
                                    <Country>
                                        <FlagImg src={country.src} alt={country.poKey} />
                                        <div>{t(country.poKey)}</div>
                                    </Country>
                                </Select>
                            </CountryItem>
                        ))}
                    </CountryList>
                </StyledModalBody>
                <StyledModalFooter>
                    <ActionButton
                        data-testid={OneWestwingFooterIds.owwCountrySelectorOverlaySubmit}
                        onClick={handleRedirect}
                    >
                        {t('continue')}
                    </ActionButton>
                </StyledModalFooter>
            </StyledModalContent>
        </Modal>
    );
};
